import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Paper,
  Slider,
} from '@mui/material';
import { ShoppingCart as CartIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  setProducts,
  setLoading,
  setError,
} from '../store/slices/productSlice';
import { productAPI } from '../services/api';
import Loading from '../components/common/Loading';

const Products = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { products, loading, error } = useSelector((state) => state.products);
  const [categories, setCategories] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 0 });
  const [filters, setFilters] = useState({
    category: '',
    priceRange: [0, 0],
  });

  useEffect(() => {
    loadProducts();
  }, []);

  // Update price range and extract categories when products change
  useEffect(() => {
    if (products.length > 0) {
      // Update price range
      const prices = products.map(product => product.price);
      const minPrice = Math.floor(Math.min(...prices));
      const maxPrice = Math.ceil(Math.max(...prices));
      setPriceRange({ min: minPrice, max: maxPrice });
      setFilters(prev => ({
        ...prev,
        priceRange: [minPrice, maxPrice]
      }));
      
      // Extract unique categories
      const uniqueCategories = [];
      const categoryMap = new Map();
      
      products.forEach(product => {
        const categoryId = product.category._id;
        const categoryName = product.category.name;
        
        if (!categoryMap.has(categoryId)) {
          categoryMap.set(categoryId, true);
          uniqueCategories.push({
            _id: categoryId,
            name: categoryName
          });
        }
      });
      
      setCategories(uniqueCategories);
    }
  }, [products]);

  const loadProducts = async () => {
    try {
      dispatch(setLoading(true));
      const response = await productAPI.getAll();
      if (Array.isArray(response.data)) {
        const transformedProducts = response.data.map(product => ({
          ...product,
          category: {
            _id: product.category,
            name: product.category
          }
        }));
        dispatch(setProducts(transformedProducts));
      } else if (response.data && response.data.data) {
        const transformedProducts = response.data.data.map(product => ({
          ...product,
          category: {
            _id: product.category,
            name: product.category
          }
        }));
        dispatch(setProducts(transformedProducts));
      } else {
        dispatch(setError('Invalid response format'));
        toast.error('Failed to load products');
      }
    } catch (error) {
      dispatch(setError(error.message));
      toast.error('Failed to load products');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePriceChange = (event, newValue) => {
    setFilters(prev => ({
      ...prev,
      priceRange: newValue
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      category: '',
      priceRange: [priceRange.min, priceRange.max]
    });
  };

  const filteredProducts = products.filter(product => {
    const matchesCategory = !filters.category || product.category._id === filters.category;
    const matchesPrice = product.price >= filters.priceRange[0] && product.price <= filters.priceRange[1];
    return matchesCategory && matchesPrice;
  });

  if (loading) {
    return <Loading message="Loading products..." />;
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Our Products
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Discover our collection of products
        </Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                label="Category"
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box>
              <Typography gutterBottom>
                Price Range: ${filters.priceRange[0]} - ${filters.priceRange[1]}
              </Typography>
              <Slider
                value={filters.priceRange}
                onChange={handlePriceChange}
                valueLabelDisplay="auto"
                min={priceRange.min}
                max={priceRange.max}
                step={1}
                marks={[
                  { value: priceRange.min, label: `$${priceRange.min}` },
                  { value: priceRange.max, label: `$${priceRange.max}` },
                ]}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              variant="outlined" 
              fullWidth 
              onClick={handleResetFilters}
              sx={{ height: 56 }}
            >
              Reset Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Products Grid */}
      <Grid container spacing={3}>
        {filteredProducts.length === 0 ? (
          <Grid item xs={12}>
            <Typography variant="h6" color="text.secondary" align="center">
              No products found matching your filters
            </Typography>
          </Grid>
        ) : (
          filteredProducts.map((product) => (
            <Grid item key={product._id} xs={12} sm={6} md={4}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3,
                  }
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={product.image || 'https://via.placeholder.com/200x200?text=No+Image'}
                  alt={product.name}
                  sx={{ objectFit: 'cover' }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h6" component="h2">
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {product.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" color="primary" sx={{ mr: 1 }}>
                      ${product.price.toFixed(2)}
                    </Typography>
                    <Chip
                      label={product.category.name}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Stock: {product.stock}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<CartIcon />}
                    onClick={() => navigate(`/products/${product._id}`)}
                  >
                    View Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </Container>
  );
};

export default Products; 